import {
  CircleCheck, CircleHelp, CircleX, OctagonAlert, TriangleAlert, Wrench, type LucideIcon,
} from "lucide-react";

import type { IncidentSeverity, MonitorStatus } from "@/api/generated";

// Every state has a colour AND an icon, never colour alone (PRD §5.2).
export const STATUS: Record<MonitorStatus, { label: string; icon: LucideIcon; text: string; bg: string; rank: number }> = {
  DOWN: { label: "Down", icon: CircleX, text: "text-status-down", bg: "bg-status-down", rank: 5 },
  CRITICAL: { label: "Critical", icon: OctagonAlert, text: "text-status-critical", bg: "bg-status-critical", rank: 4 },
  DEGRADED: { label: "Degraded", icon: TriangleAlert, text: "text-status-degraded", bg: "bg-status-degraded", rank: 3 },
  UNKNOWN: { label: "Unknown", icon: CircleHelp, text: "text-status-unknown", bg: "bg-status-unknown", rank: 2 },
  MAINTENANCE: { label: "Maintenance", icon: Wrench, text: "text-status-maintenance", bg: "bg-status-maintenance", rank: 1 },
  UP: { label: "Up", icon: CircleCheck, text: "text-status-up", bg: "bg-status-up", rank: 0 },
};

export const SEVERITY: Record<IncidentSeverity, { label: string; status: MonitorStatus; rank: number }> = {
  DOWN: { label: "Down", status: "DOWN", rank: 3 },
  CRITICAL: { label: "Critical", status: "CRITICAL", rank: 2 },
  WARNING: { label: "Warning", status: "DEGRADED", rank: 1 },
};

export const STATUS_ORDER: MonitorStatus[] = ["DOWN", "CRITICAL", "DEGRADED", "UNKNOWN", "MAINTENANCE", "UP"];

export function worst(statuses: MonitorStatus[]): MonitorStatus | undefined {
  let best: MonitorStatus | undefined;
  for (const s of statuses) {
    if (!best || STATUS[s].rank > STATUS[best].rank) best = s;
  }
  return best;
}

/** Link/node health from its monitors (ADR: worst; MAINTENANCE if all in maintenance). */
export function rollup(statuses: MonitorStatus[]): MonitorStatus {
  if (statuses.length === 0) return "UNKNOWN";
  if (statuses.every((s) => s === "MAINTENANCE")) return "MAINTENANCE";
  const known = statuses.filter((s) => s !== "MAINTENANCE");
  return worst(known) ?? "UNKNOWN";
}
